var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useMemo, useEffect, useCallback } from 'react';
var PERIOD_OPTIONS = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'last_7d', label: 'Últimos 7 dias' },
    { value: 'last_14d', label: 'Últimos 14 dias' },
    { value: 'last_30d', label: 'Últimos 30 dias' },
    { value: 'maximum', label: 'Período máximo' },
];
var VIEW_TYPE_OPTIONS = [
    { value: 'desempenho', label: 'Desempenho' },
    { value: 'conversoes', label: 'Conversões' },
];
var COLUMNS_CONFIG = {
    status: { label: 'Status', align: 'left' },
    nome: { label: 'Nome', align: 'left' },
    orcamento: { label: 'Orçamento', align: 'right' },
    gasto: { label: 'Gasto', align: 'right' },
    impressoes: { label: 'Impressões', align: 'right' },
    cliques: { label: 'Cliques', align: 'right' },
    ctr: { label: 'CTR', align: 'right' },
    cpc: { label: 'CPC Médio', align: 'right' },
    conversoes: { label: 'Conversões', align: 'right' },
    custo_por_conversao: { label: 'Custo/Conversão', align: 'right' },
    taxa_conversao: { label: 'Taxa de Conversão', align: 'right' },
};
var DEFAULT_COLUMN_VISIBILITY = {
    desempenho: ['status', 'nome', 'orcamento', 'gasto', 'impressoes', 'cliques', 'ctr', 'cpc'],
    conversoes: ['status', 'nome', 'orcamento', 'gasto', 'impressoes', 'cliques', 'ctr', 'cpc', 'conversoes', 'custo_por_conversao', 'taxa_conversao'],
};
var STATUS_COLORS = {
    ACTIVE: { bg: 'bg-green-100', text: 'text-green-700', label: 'Ativo' },
    ENABLED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Ativo' },
    PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pausado' },
    REMOVED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Removido' },
};
var formatCurrency = function (value) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};
var formatNumber = function (value) {
    return new Intl.NumberFormat('pt-BR').format(value);
};
var formatPercent = function (value) {
    return "".concat(value.toFixed(2), "%");
};
// Storage key generator
var getStorageKey = function (agencyId, clientId, platform) {
    return "google_campaigns_prefs_".concat(agencyId, "_").concat(clientId, "_").concat(platform);
};
// Modal Component
var Modal = function (_a) {
    var open = _a.open, onClose = _a.onClose, children = _a.children, title = _a.title, _b = _a.maxWidth, maxWidth = _b === void 0 ? 'max-w-[95vw]' : _b;
    if (!open)
        return null;
    return (React.createElement("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40", onClick: function (e) {
            if (e.target === e.currentTarget)
                onClose();
        } },
        React.createElement("div", { className: "bg-[#f5f6fa] rounded-lg shadow-lg w-full ".concat(maxWidth, " relative max-h-[90vh] overflow-y-auto") },
            React.createElement("button", { className: "absolute top-3 right-6 text-gray-400 hover:text-gray-700 text-2xl z-10", onClick: onClose, "aria-label": "Fechar" }, "\u00D7"),
            title && React.createElement("div", { className: "px-6 pt-1 md:pt-6 pb-1 md:pb-2 text-lg font-semibold text-[#1c1e21]" }, title),
            React.createElement("div", { className: "p-0.5 md:p-6 md:pb-10" }, children))));
};
// Tab Button Component
var TabButton = function (_a) {
    var active = _a.active, onClick = _a.onClick, label = _a.label, count = _a.count, icon = _a.icon;
    return (React.createElement("button", { onClick: onClick, className: "px-1 md:px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 transition-colors ".concat(active ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100') },
        icon,
        label,
        count !== undefined && count > 0 && (React.createElement("span", { className: "bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full" }, count))));
};
// Status Badge Component
var StatusBadge = function (_a) {
    var status = _a.status;
    var normalizedStatus = status.toUpperCase();
    var config = STATUS_COLORS[normalizedStatus] || STATUS_COLORS.PAUSED;
    return (React.createElement("span", { className: "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ".concat(config.bg, " ").concat(config.text) },
        React.createElement("span", { className: "w-2 h-2 rounded-full mr-1.5 ".concat(normalizedStatus === 'ACTIVE' || normalizedStatus === 'ENABLED' ? 'bg-green-500' : 'bg-gray-400') }),
        config.label));
};
// Draggable Header Component
var DraggableHeader = function (_a) {
    var col = _a.col, label = _a.label, align = _a.align, stickyClass = _a.stickyClass, onDragStart = _a.onDragStart, onDragOver = _a.onDragOver, onDragEnd = _a.onDragEnd, isDragging = _a.isDragging, isOver = _a.isOver;
    var isFixed = col === 'status' || col === 'nome';
    return (React.createElement("th", { draggable: !isFixed, onDragStart: function () { return !isFixed && onDragStart(col); }, onDragOver: function (e) { return !isFixed && onDragOver(e, col); }, onDragEnd: onDragEnd, className: "px-3 py-3 font-medium text-gray-700 whitespace-nowrap select-none border border-gray-300\n        ".concat(align === 'right' ? 'text-right' : 'text-left', " \n        ").concat(stickyClass, "\n        ").concat(!isFixed ? 'cursor-grab active:cursor-grabbing' : '', "\n        ").concat(isDragging ? 'opacity-50' : '', "\n        ").concat(isOver ? 'bg-blue-100 border-l-2 border-blue-500' : '', "\n      ") },
        React.createElement("div", { className: "flex items-center gap-1" },
            !isFixed && (React.createElement("svg", { className: "w-3 h-3 text-gray-400 flex-shrink-0", fill: "currentColor", viewBox: "0 0 20 20" },
                React.createElement("path", { d: "M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" }))),
            React.createElement("span", null, label))));
};
var GoogleCampaigns = function (_a) {
    var data = _a.data, open = _a.open, onClose = _a.onClose, _b = _a.title, title = _b === void 0 ? "Campanhas do Google ADS Ativas" : _b, maxWidth = _a.maxWidth, _c = _a.agencyId, agencyId = _c === void 0 ? 'default' : _c, _d = _a.clientId, clientId = _d === void 0 ? 'default' : _d, _e = _a.platform, platform = _e === void 0 ? 'google' : _e, onEditCampaign = _a.onEditCampaign;
    var _f = useState('campaigns'), activeTab = _f[0], setActiveTab = _f[1];
    var _g = useState('today'), selectedPeriod = _g[0], setSelectedPeriod = _g[1];
    var _h = useState('conversoes'), viewType = _h[0], setViewType = _h[1];
    var _j = useState(new Set()), selectedCampaigns = _j[0], setSelectedCampaigns = _j[1];
    var _k = useState(new Set()), selectedAdGroups = _k[0], setSelectedAdGroups = _k[1];
    var _l = useState(new Set()), selectedAds = _l[0], setSelectedAds = _l[1];
    var _m = useState({}), customColumnOrder = _m[0], setCustomColumnOrder = _m[1];
    var _o = useState(false), showRotateModal = _o[0], setShowRotateModal = _o[1];
    // Drag state
    var _p = useState(null), draggedColumn = _p[0], setDraggedColumn = _p[1];
    var _q = useState(null), dragOverColumn = _q[0], setDragOverColumn = _q[1];
    var campaigns = data || [];
    var storageKey = getStorageKey(agencyId, clientId, platform);
    // Load preferences from localStorage
    useEffect(function () {
        try {
            var stored = localStorage.getItem(storageKey);
            if (stored) {
                var prefs = JSON.parse(stored);
                if (prefs.viewType)
                    setViewType(prefs.viewType);
                if (prefs.columnOrder)
                    setCustomColumnOrder(prefs.columnOrder);
            }
        }
        catch (e) {
            console.error('Error loading preferences:', e);
        }
    }, [storageKey]);
    // Save preferences to localStorage
    var savePreferences = useCallback(function (newViewType, newColumnOrder) {
        try {
            var prefs = {
                viewType: newViewType !== null && newViewType !== void 0 ? newViewType : viewType,
                columnOrder: newColumnOrder !== null && newColumnOrder !== void 0 ? newColumnOrder : customColumnOrder,
            };
            localStorage.setItem(storageKey, JSON.stringify(prefs));
        }
        catch (e) {
            console.error('Error saving preferences:', e);
        }
    }, [storageKey, viewType, customColumnOrder]);
    // Handle view type change
    var handleViewTypeChange = function (newViewType) {
        setViewType(newViewType);
        savePreferences(newViewType, customColumnOrder);
    };
    // Helpers
    var getMetrics = function (item) {
        return item.metrics[selectedPeriod] || item.metrics.today;
    };
    var getEntityBudget = function (item) {
        if (activeTab === 'campaigns')
            return item.daily_budget;
        return undefined;
    };
    // Handle row click - navigate to next tab
    var handleRowClick = function (item, e) {
        // Ignore if clicking on checkbox or link
        var target = e.target;
        if (target.tagName === 'INPUT' || target.tagName === 'A' || target.closest('a')) {
            return;
        }
        if (activeTab === 'campaigns') {
            var campaignId = String(item.campaign_id);
            setSelectedCampaigns(new Set([campaignId]));
            setSelectedAdGroups(new Set());
            setActiveTab('adgroups');
        }
        else if (activeTab === 'adgroups') {
            var adGroupId = item.ad_group_id;
            setSelectedAdGroups(new Set([adGroupId]));
            setActiveTab('ads');
        }
    };
    // Filtered data based on hierarchical selection
    var filteredAdGroups = useMemo(function () {
        var allAdGroups = campaigns.flatMap(function (campaign) {
            return (campaign.ad_groups || []).map(function (adGroup) { return (__assign(__assign({}, adGroup), { campaign_name: campaign.campaign_name, campaign_id: String(campaign.campaign_id) })); });
        });
        if (selectedCampaigns.size > 0) {
            return allAdGroups.filter(function (adGroup) { return selectedCampaigns.has(adGroup.campaign_id); });
        }
        return allAdGroups;
    }, [campaigns, selectedCampaigns]);
    var filteredAds = useMemo(function () {
        var allAds = campaigns.flatMap(function (campaign) {
            return (campaign.ad_groups || []).flatMap(function (adGroup) {
                return (adGroup.ads || []).map(function (ad) { return (__assign(__assign({}, ad), { campaign_name: campaign.campaign_name, campaign_id: String(campaign.campaign_id), ad_group_name: adGroup.ad_group_name, ad_group_id: adGroup.ad_group_id })); });
            });
        });
        if (selectedAdGroups.size > 0) {
            return allAds.filter(function (ad) { return selectedAdGroups.has(ad.ad_group_id); });
        }
        if (selectedCampaigns.size > 0) {
            return allAds.filter(function (ad) { return selectedCampaigns.has(ad.campaign_id); });
        }
        return allAds;
    }, [campaigns, selectedCampaigns, selectedAdGroups]);
    // Selection management
    var getSelectedSet = function () {
        if (activeTab === 'campaigns')
            return selectedCampaigns;
        if (activeTab === 'adgroups')
            return selectedAdGroups;
        return selectedAds;
    };
    var setSelectedSet = function (newSet) {
        if (activeTab === 'campaigns')
            setSelectedCampaigns(newSet);
        else if (activeTab === 'adgroups')
            setSelectedAdGroups(newSet);
        else
            setSelectedAds(newSet);
    };
    var toggleItemSelection = function (itemId, e) {
        e.stopPropagation();
        var currentSet = getSelectedSet();
        var newSet = new Set(currentSet);
        if (newSet.has(itemId)) {
            newSet.delete(itemId);
        }
        else {
            newSet.add(itemId);
        }
        setSelectedSet(newSet);
    };
    var toggleAllItems = function () {
        var currentSet = getSelectedSet();
        var allIds = [];
        if (activeTab === 'campaigns')
            allIds = campaigns.map(function (c) { return String(c.campaign_id); });
        else if (activeTab === 'adgroups')
            allIds = filteredAdGroups.map(function (a) { return a.ad_group_id; });
        else
            allIds = filteredAds.map(function (a) { return a.ad_id; });
        if (currentSet.size === allIds.length) {
            setSelectedSet(new Set());
        }
        else {
            setSelectedSet(new Set(allIds));
        }
    };
    var currentItems = activeTab === 'campaigns' ? campaigns : activeTab === 'adgroups' ? filteredAdGroups : filteredAds;
    var currentSelectedSet = getSelectedSet();
    // Visible columns with custom order support
    var visibleColumns = useMemo(function () {
        var defaultColumns = __spreadArray([], DEFAULT_COLUMN_VISIBILITY[viewType], true);
        var customOrder = customColumnOrder[viewType];
        if (customOrder && customOrder.length > 0) {
            // Merge custom order with any new columns
            var orderedCols = customOrder.filter(function (col) { return defaultColumns.includes(col); });
            var newCols = defaultColumns.filter(function (col) { return !customOrder.includes(col); });
            return __spreadArray(__spreadArray([], orderedCols, true), newCols, true);
        }
        return defaultColumns;
    }, [viewType, customColumnOrder]);
    // Drag and drop handlers
    var handleDragStart = function (col) {
        setDraggedColumn(col);
    };
    var handleDragOver = function (e, col) {
        e.preventDefault();
        if (col !== draggedColumn && col !== 'status' && col !== 'nome') {
            setDragOverColumn(col);
        }
    };
    var handleDragEnd = function () {
        var _a;
        if (draggedColumn && dragOverColumn && draggedColumn !== dragOverColumn) {
            var newOrder = __spreadArray([], visibleColumns, true);
            var dragIndex = newOrder.indexOf(draggedColumn);
            var dropIndex = newOrder.indexOf(dragOverColumn);
            if (dragIndex !== -1 && dropIndex !== -1) {
                newOrder.splice(dragIndex, 1);
                newOrder.splice(dropIndex, 0, draggedColumn);
                var newColumnOrder = __assign(__assign({}, customColumnOrder), (_a = {}, _a[viewType] = newOrder, _a));
                setCustomColumnOrder(newColumnOrder);
                savePreferences(viewType, newColumnOrder);
            }
        }
        setDraggedColumn(null);
        setDragOverColumn(null);
    };
    // Calculate totals
    var totals = useMemo(function () {
        return currentItems.reduce(function (acc, item) {
            var metrics = getMetrics(item);
            var budget = getEntityBudget(item);
            return {
                impressions: acc.impressions + (metrics.impressions || 0),
                clicks: acc.clicks + (metrics.clicks || 0),
                spend: acc.spend + (metrics.spend || 0),
                conversions: acc.conversions + (metrics.conversions || 0),
                total_budget: acc.total_budget + (budget || 0),
            };
        }, {
            impressions: 0,
            clicks: 0,
            spend: 0,
            conversions: 0,
            total_budget: 0,
        });
    }, [currentItems, selectedPeriod, activeTab]);
    // Render metric cell
    var renderMetricCell = function (key, metrics, budget) {
        var spend = metrics.spend || 0;
        var clicks = metrics.clicks || 0;
        var impressions = metrics.impressions || 0;
        var conversions = metrics.conversions || 0;
        var ctr = metrics.ctr || (impressions > 0 ? (clicks / impressions) * 100 : 0);
        var cpc = metrics.cpc || (clicks > 0 ? spend / clicks : 0);
        var costPerConversion = metrics.cost_per_conversion || (conversions > 0 ? spend / conversions : 0);
        var taxaConversao = clicks > 0 ? (conversions / clicks) * 100 : 0;
        switch (key) {
            case 'orcamento':
                return budget !== undefined && budget > 0 ? "".concat(formatCurrency(budget), "/dia") : '—';
            case 'gasto':
                return formatCurrency(spend);
            case 'impressoes':
                return formatNumber(impressions);
            case 'cliques':
                return formatNumber(clicks);
            case 'ctr':
                return formatPercent(ctr);
            case 'cpc':
                return clicks > 0 ? formatCurrency(cpc) : '—';
            case 'conversoes':
                return formatNumber(conversions);
            case 'custo_por_conversao':
                return conversions > 0 ? formatCurrency(costPerConversion) : '—';
            case 'taxa_conversao':
                return formatPercent(taxaConversao);
            default:
                return '—';
        }
    };
    // Render total cell
    var renderTotalCell = function (key) {
        var spend = totals.spend, clicks = totals.clicks, impressions = totals.impressions, conversions = totals.conversions, total_budget = totals.total_budget;
        var ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
        var cpc = clicks > 0 ? spend / clicks : 0;
        var costPerConversion = conversions > 0 ? spend / conversions : 0;
        var taxaConversao = clicks > 0 ? (conversions / clicks) * 100 : 0;
        switch (key) {
            case 'orcamento':
                return total_budget > 0 ? "".concat(formatCurrency(total_budget), "/dia") : '—';
            case 'gasto':
                return formatCurrency(spend);
            case 'impressoes':
                return formatNumber(impressions);
            case 'cliques':
                return formatNumber(clicks);
            case 'ctr':
                return formatPercent(ctr);
            case 'cpc':
                return clicks > 0 ? formatCurrency(cpc) : '—';
            case 'conversoes':
                return formatNumber(conversions);
            case 'custo_por_conversao':
                return conversions > 0 ? formatCurrency(costPerConversion) : '—';
            case 'taxa_conversao':
                return formatPercent(taxaConversao);
            default:
                return '—';
        }
    };
    // Get column label
    var getColumnLabel = function (key) {
        var _a;
        return ((_a = COLUMNS_CONFIG[key]) === null || _a === void 0 ? void 0 : _a.label) || key;
    };
    // Get column alignment
    var getColumnAlign = function (key) {
        var _a;
        return ((_a = COLUMNS_CONFIG[key]) === null || _a === void 0 ? void 0 : _a.align) || 'left';
    };
    // Get entity name
    var getEntityName = function (item) {
        if (activeTab === 'campaigns') {
            return {
                primary: item.campaign_name,
            };
        }
        if (activeTab === 'adgroups') {
            return {
                primary: item.ad_group_name,
                secondary: item.campaign_name,
            };
        }
        return {
            primary: item.ad_name,
            secondary: "".concat(item.ad_group_name, " \u2022 ").concat(item.campaign_name),
        };
    };
    // Get entity ID
    var getEntityId = function (item) {
        if (activeTab === 'campaigns')
            return String(item.campaign_id);
        if (activeTab === 'adgroups')
            return item.ad_group_id;
        return item.ad_id;
    };
    // Render name cell
    var renderNameCell = function (item, entityName) {
        var entityType = activeTab === 'campaigns' ? 'campaign' : activeTab === 'adgroups' ? 'adset' : 'ad';
        return (React.createElement("td", { key: "nome", className: "px-1 md:px-3 py-3 sticky left-[24px] md:left-[120px] z-10 bg-inherit border border-gray-300" },
            React.createElement("div", { className: "flex items-center gap-3" },
                React.createElement("div", { className: "flex-1 min-w-0" },
                    React.createElement("div", { className: "flex items-center gap-1 group/name" },
                        React.createElement("span", { className: "font-medium text-[#4285f4] hover:underline block truncate cursor-pointer", title: entityName.primary }, entityName.primary),
                        onEditCampaign && (React.createElement("button", { onClick: function (e) {
                                e.stopPropagation();
                                onEditCampaign(item, entityType);
                            }, className: "opacity-0 group-hover/name:opacity-100 transition-opacity p-1 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600 flex-shrink-0", title: "Editar ".concat(entityType === 'campaign' ? 'campanha' : entityType === 'adset' ? 'grupo de anúncios' : 'anúncio') },
                            React.createElement("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                                React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }))))),
                    entityName.secondary && (React.createElement("span", { className: "block text-xs text-[#444] truncate" }, entityName.secondary))))));
    };
    // Tab labels
    var tabLabels = {
        campaigns: { singular: 'campanha', plural: 'campanhas' },
        adgroups: { singular: 'grupo de anúncios', plural: 'grupos de anúncios' },
        ads: { singular: 'anúncio', plural: 'anúncios' },
    };
    // Filter info
    var getFilterInfo = function () {
        if (activeTab === 'adgroups' && selectedCampaigns.size > 0) {
            return "Filtrando por ".concat(selectedCampaigns.size, " campanha(s) selecionada(s)");
        }
        if (activeTab === 'ads') {
            if (selectedAdGroups.size > 0) {
                return "Filtrando por ".concat(selectedAdGroups.size, " grupo(s) de an\u00FAncios selecionado(s)");
            }
            if (selectedCampaigns.size > 0) {
                return "Filtrando por ".concat(selectedCampaigns.size, " campanha(s) selecionada(s)");
            }
        }
        return null;
    };
    var filterInfo = getFilterInfo();
    // Reset column order
    var resetColumnOrder = function () {
        var _a;
        var newColumnOrder = __assign(__assign({}, customColumnOrder), (_a = {}, _a[viewType] = DEFAULT_COLUMN_VISIBILITY[viewType], _a));
        setCustomColumnOrder(newColumnOrder);
        savePreferences(viewType, newColumnOrder);
    };
    useEffect(function () {
        if (open) {
            var checkOrientation_1 = function () {
                if (window.innerWidth < 667) {
                    setShowRotateModal(true);
                }
                else {
                    setShowRotateModal(false);
                }
            };
            checkOrientation_1();
            window.addEventListener('resize', checkOrientation_1);
            return function () { return window.removeEventListener('resize', checkOrientation_1); };
        }
        else {
            setShowRotateModal(false);
        }
    }, [open]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: open && !showRotateModal, onClose: onClose, title: title, maxWidth: maxWidth },
            React.createElement("div", { className: "bg-[#f5f6fa] rounded-lg w-full max-w-full overflow-hidden" },
                React.createElement("div", { className: "border-b border-gray-200" },
                    React.createElement("div", { className: "flex items-center justify-between px-1 md:px-4 py-1 md:py-2 bg-gray-50 flex-wrap gap-2" },
                        React.createElement("div", { className: "flex items-center gap-0 md:gap-2" },
                            React.createElement(TabButton, { active: activeTab === 'campaigns', onClick: function () { return setActiveTab('campaigns'); }, label: "Campanhas", count: selectedCampaigns.size > 0 ? selectedCampaigns.size : undefined }),
                            React.createElement(TabButton, { active: activeTab === 'adgroups', onClick: function () { return setActiveTab('adgroups'); }, label: "Grupos de an\u00FAncios", count: selectedAdGroups.size > 0 ? selectedAdGroups.size : undefined }),
                            React.createElement(TabButton, { active: activeTab === 'ads', onClick: function () { return setActiveTab('ads'); }, label: "An\u00FAncios", count: selectedAds.size > 0 ? selectedAds.size : undefined })),
                        React.createElement("div", { className: "flex items-center gap-0 md:gap-3" },
                            React.createElement("button", { onClick: resetColumnOrder, className: "hidden md:block px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded", title: "Resetar ordem das colunas" },
                                React.createElement("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                                    React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }))),
                            React.createElement("select", { value: viewType, onChange: function (e) { return handleViewTypeChange(e.target.value); }, className: "px-3 py-2 text-gray-600 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" }, VIEW_TYPE_OPTIONS.map(function (option) { return (React.createElement("option", { key: option.value, value: option.value }, option.label)); })),
                            React.createElement("select", { value: selectedPeriod, onChange: function (e) { return setSelectedPeriod(e.target.value); }, className: "px-3 py-2 text-gray-600 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" }, PERIOD_OPTIONS.map(function (option) { return (React.createElement("option", { key: option.value, value: option.value }, option.label)); })))),
                    filterInfo && (React.createElement("div", { className: "px-4 py-2 bg-blue-50 border-t border-blue-100 flex items-center justify-between" },
                        React.createElement("span", { className: "text-sm text-blue-700" }, filterInfo),
                        React.createElement("button", { onClick: function () {
                                setSelectedCampaigns(new Set());
                                setSelectedAdGroups(new Set());
                            }, className: "text-sm text-blue-600 hover:text-blue-800 underline" }, "Limpar filtros"))),
                    React.createElement("div", { className: "px-4 py-1 bg-gray-100 border-t border-gray-200" },
                        React.createElement("span", { className: "text-xs text-gray-500" }, "\uD83D\uDCA1 Arraste as colunas para reorganizar \u2022 Clique na linha para navegar"))),
                React.createElement("div", { className: "overflow-x-auto" },
                    React.createElement("table", { className: "w-full text-sm text-[#1c1e21]" },
                        React.createElement("thead", { className: "bg-[#f5f6fa] border-b border-gray-200" },
                            React.createElement("tr", null,
                                React.createElement("th", { className: "w-10 px-1 md:px-3 py-3 text-left sticky left-0 z-20 bg-[#f5f6fa] border border-gray-300" },
                                    React.createElement("input", { type: "checkbox", checked: currentSelectedSet.size === currentItems.length && currentItems.length > 0, onChange: toggleAllItems, className: "w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" })),
                                visibleColumns.map(function (col) {
                                    var label = getColumnLabel(col);
                                    var align = getColumnAlign(col);
                                    var stickyClass = col === 'status'
                                        ? 'hidden md:table-cell sticky left-[23px] md:left-[40px] z-20 bg-[#f5f6fa] border border-gray-300'
                                        : col === 'nome'
                                            ? 'sticky left-[24px] md:left-[120px] z-20 bg-[#f5f6fa] min-w-[250px] border border-gray-300'
                                            : '';
                                    return (React.createElement(DraggableHeader, { key: col, col: col, label: col === 'nome' ? tabLabels[activeTab].singular.charAt(0).toUpperCase() + tabLabels[activeTab].singular.slice(1) : label, align: align, stickyClass: stickyClass, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, isDragging: draggedColumn === col, isOver: dragOverColumn === col }));
                                }))),
                        React.createElement("tbody", null, currentItems.map(function (item, index) {
                            var metrics = getMetrics(item);
                            var entityId = getEntityId(item);
                            var entityName = getEntityName(item);
                            var budget = getEntityBudget(item);
                            var rowBg = index % 2 === 0 ? 'bg-[#f5f6fa]' : 'bg-[#e8e9eb]';
                            var isSelected = currentSelectedSet.has(entityId);
                            return (React.createElement("tr", { key: entityId, onClick: function (e) { return handleRowClick(item, e); }, className: "".concat(rowBg, " hover:bg-[#e4e6eb] cursor-pointer transition-colors ").concat(isSelected ? 'bg-blue-50 hover:bg-blue-100' : '') },
                                React.createElement("td", { className: "px-1 md:px-3 py-3 sticky left-0 z-10 bg-inherit border border-gray-300" },
                                    React.createElement("input", { type: "checkbox", checked: isSelected, onChange: function (e) { return toggleItemSelection(entityId, e); }, onClick: function (e) { return e.stopPropagation(); }, className: "w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" })),
                                visibleColumns.map(function (col) {
                                    var align = getColumnAlign(col);
                                    if (col === 'status') {
                                        return (React.createElement("td", { key: col, className: "hidden md:table-cell px-1 md:px-3 py-3 sticky left-[23px] md:left-[40px] z-10 bg-inherit" },
                                            React.createElement(StatusBadge, { status: item.status })));
                                    }
                                    if (col === 'nome') {
                                        return renderNameCell(item, entityName);
                                    }
                                    return (React.createElement("td", { key: col, className: "px-3 py-3 whitespace-nowrap border border-gray-300 ".concat(align === 'right' ? 'text-right' : '') }, renderMetricCell(col, metrics, budget)));
                                })));
                        })),
                        React.createElement("tfoot", { className: "bg-gray-100 border-t-2 border-gray-300" },
                            React.createElement("tr", { className: "font-semibold" },
                                React.createElement("td", { className: "px-1 md:px-3 py-3 sticky left-0 z-10 bg-[#f5f6fa] border border-gray-300" }),
                                visibleColumns.map(function (col) {
                                    var align = getColumnAlign(col);
                                    if (col === 'status') {
                                        return React.createElement("td", { key: col, className: "hidden md:table-cell px-1 md:px-3 py-3 sticky left-[23px] md:left-[40px] z-10 bg-[#f5f6fa] border border-gray-300" });
                                    }
                                    if (col === 'nome') {
                                        return (React.createElement("td", { key: col, className: "px-1 md:px-3 py-3 sticky left-[24px] md:left-[120px] z-30 text-gray-700 bg-[#f5f6fa] border border-gray-300" },
                                            "Resultados de ",
                                            currentItems.length,
                                            " ",
                                            currentItems.length === 1 ? tabLabels[activeTab].singular : tabLabels[activeTab].plural));
                                    }
                                    return (React.createElement("td", { key: col, className: "px-3 py-3 whitespace-nowrap border border-gray-300 ".concat(align === 'right' ? 'text-right' : '') }, renderTotalCell(col)));
                                }))))),
                currentItems.length === 0 && (React.createElement("div", { className: "p-8 text-center text-gray-500" },
                    React.createElement("svg", { className: "w-16 h-16 mx-auto mb-4 text-gray-300", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" })),
                    React.createElement("p", { className: "text-lg font-medium" },
                        "Nenhum ",
                        tabLabels[activeTab].singular,
                        " encontrado"),
                    React.createElement("p", { className: "text-sm" }, filterInfo
                        ? 'Tente limpar os filtros para ver mais resultados.'
                        : "Os ".concat(tabLabels[activeTab].plural, " aparecer\u00E3o aqui quando dispon\u00EDveis.")))))),
        showRotateModal && (React.createElement("div", { className: "fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60" },
            React.createElement("div", { className: "bg-white rounded-lg shadow-lg p-8 max-w-xs text-center flex flex-col items-center" },
                React.createElement("svg", { className: "w-16 h-16 mb-4", viewBox: "0 0 265 213", fill: "none", style: {
                        animation: 'rotatePhone 1.2s infinite alternate cubic-bezier(0.4,0,0.2,1)'
                    }, xmlns: "http://www.w3.org/2000/svg" },
                    React.createElement("g", { transform: "translate(0,213) scale(0.1,-0.1)", fill: "#000000", stroke: "none" },
                        React.createElement("path", { d: "M240 1877 c-9 -7 -24 -23 -33 -37 -16 -22 -17 -84 -17 -766 l0 -741\n                  25 -27 c13 -14 36 -31 50 -36 15 -6 137 -10 281 -10 l255 0 -3 92 -3 93 -282\n                  3 -283 2 0 630 c0 346 3 630 8 632 4 2 163 2 355 0 l347 -3 0 -339 0 -340 25\n                  0 25 0 0 386 c0 399 -1 405 -43 452 -20 22 -21 22 -356 22 -249 -1 -340 -4\n                  -351 -13z" }),
                        React.createElement("path", { d: "M1082 1661 l3 -43 65 -4 c89 -6 155 -31 210 -81 60 -54 103 -145 104\n                  -222 1 -49 -1 -55 -22 -60 -22 -6 -22 -7 23 -83 25 -43 49 -78 53 -78 5 0 74\n                  115 90 153 2 4 -7 7 -20 7 -22 0 -25 6 -37 76 -19 112 -48 179 -104 241 -77\n                  83 -158 120 -285 130 l-83 6 3 -42z" }),
                        React.createElement("path", { d: "M851 965 l-31 -36 0 -322 c0 -288 2 -325 17 -347 35 -52 0 -49 800\n                  -50 l751 0 31 31 31 31 0 332 0 332 -31 32 -31 32 -753 0 -753 0 -31 -35z\n                  m1409 -360 l0 -345 -620 0 -620 0 0 345 0 345 620 0 620 0 0 -345z" })),
                    React.createElement("style", null, "\n                  @keyframes rotatePhone {\n                    0% { transform: rotate(0deg);}\n                    100% { transform: rotate(15deg);}\n                  }\n                ")),
                React.createElement("div", { className: "text-lg font-semibold mb-2 text-blue-700" }, "Gire seu celular"),
                React.createElement("div", { className: "text-gray-600 text-sm mb-4" }, "Para visualizar as campanhas, gire o celular para o modo paisagem."),
                React.createElement("button", { className: "mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600", onClick: function () { return setShowRotateModal(false); } }, "Ok, entendi"))))));
};
export default GoogleCampaigns;
